import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import last from 'lodash/last';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AvailableLanguages } from 'types/users.types';
import { getTracingEvents } from 'utils/amplitude';
import {
  WEBAPP_CATEGORYPICKER_CHOOSESEAERCHEDCATEGORY,
  WEBAPP_CATEGORYPICKER_FOCUSEDCATEGORYSEARCH,
} from 'utils/amplitude/events/global.amplitude';
import { SearchIconOutline } from 'utils/icons';
import BreadCrumbs from './BreadCrumbs.component';
import type { CategoriesPickerSharedProps } from './categoriesPicker.types';
import ListCards from './ListCards.component';
import ListQuestions from './ListQuestions.component';
import { useFilteredCategories } from './useFilteredCategories';

type AllCategoriesListCardsProps = CategoriesPickerSharedProps & {
  categoriesDict: Record<string, any>;
  category: any;
  onClick: (cate: any) => void;
  value?: string;
  categoryList: any[];
};

const AllCategoriesListCards: FC<AllCategoriesListCardsProps> = ({
  category,
  categoriesDict,
  value,
  onClick,
  tooltips,
  helpers,
  disabledCategories,
  categoryList,
}) => {
  const { t } = useTranslation();

  const isFiltered = categoryList !== category.list;

  const isEmpty = useMemo(
    () => categoryList.every((category: any) => category.list.length === 0),
    [categoryList],
  );

  if (isEmpty) return <div>{t('list_no_result')}</div>;

  return (
    <div className="flex flex-col gap-6">
      {categoryList.map((cate: any) => (
        <ListCards
          key={cate.id}
          category={cate}
          categoriesDict={categoriesDict}
          value={value}
          onClick={onClick}
          shouldGroupList={isFiltered}
          showCategoryTitle
          tooltips={tooltips}
          helpers={helpers}
          disabledCategories={disabledCategories}
        />
      ))}
    </div>
  );
};

type ListProps = CategoriesPickerSharedProps & {
  categoriesDict: Record<string, any>;
  activePath: string[];
  onPathChange: (path: string[]) => void;
  onChange: (category: any) => void;
  value?: string;
  searchValue: string;
  searchBarPlaceholder?: string;
  onSearch: (value: string) => void;
  header?: ReactNode;
  renderGridHeader?: (
    searchTerm: string,
    onChange: (category: any) => void,
  ) => ReactNode;
  hideSearchBar?: boolean;
};

const events = getTracingEvents({
  onSearchFocus: WEBAPP_CATEGORYPICKER_FOCUSEDCATEGORYSEARCH,
  onChooseSearchedCategory: {
    name: WEBAPP_CATEGORYPICKER_CHOOSESEAERCHEDCATEGORY,
    data: (category: any, displayedCategories: any[]) => ({
      category_id: category.id,
      matchRank: category.rank,
      displayRank: displayedCategories
        .reduce((acc, c) => ('list' in c ? acc.concat(c.list) : acc), []) // flatten the categories
        .findIndex((c: any) => c.id === category.id),
    }),
  },
});

const List: FC<ListProps> = ({
  categoriesDict,
  activePath,
  onPathChange,
  onChange,
  value,
  searchValue,
  header,
  renderGridHeader,
  onSearch,
  searchBarPlaceholder,
  hideSearchBar,
  tooltips,
  helpers,
  disabledCategories,
  renderHeader,
  backendSearchCategoryFn,
}) => {
  const { t, i18n } = useTranslation();

  const dataLayers = useMemo(
    () => activePath.map((id: string) => categoriesDict[id]),
    [activePath],
  );

  const currentLayer = last(dataLayers);

  const categoryList = useFilteredCategories({
    backendSearchCategoryFn,
    lang: i18n.language as AvailableLanguages,
    searchValue,
    categoriesDict,
    currentLayer,
  });

  if (!currentLayer || !categoryList) return null; // for safty if the value doesn't exist on our data for some reason

  const backBtn = (
    <BreadCrumbs
      titleClassName="text-sm"
      iconClassName="w-4"
      data={dataLayers.map((layer) => ({
        label: layer.title,
        value: layer.path,
      }))}
      onClick={(path: any) => {
        onPathChange(path);
      }}
    />
  );

  const onClick = (category: any) => {
    if (category.similarity) {
      events.onChooseSearchedCategory(category, categoryList);
    }
    if (category.isFinal) return onChange(categoriesDict[category.id]);
    onPathChange(category.path);
  };

  const searchElm = (
    <div className="flex items-center relative w-full">
      <FreeTextInput
        onChange={({ target: { value } }) => {
          onSearch(value);
        }}
        Icon={SearchIconOutline}
        onFocus={() => events.onSearchFocus()}
        value={searchValue}
        type="text"
        placeholder={
          searchBarPlaceholder || t('invoice.category_picker.placeholder')
        }
        wrapperClassName="w-full"
      />
    </div>
  );

  if (currentLayer.isAllCategories) {
    return (
      <>
        <div className="flex flex-col gap-4 mb-4">
          {backBtn}
          {header}
          {!hideSearchBar && searchElm}
          {renderGridHeader?.(searchValue.trim(), onClick)}
          {renderHeader?.(currentLayer)}
        </div>

        <AllCategoriesListCards
          category={currentLayer}
          categoriesDict={categoriesDict}
          value={value}
          onClick={onClick}
          tooltips={tooltips}
          helpers={helpers}
          disabledCategories={disabledCategories}
          categoryList={categoryList}
          backendSearchCategoryFn={backendSearchCategoryFn}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 mb-4">
        {backBtn}
        {header}
        {!hideSearchBar && searchElm}
        {renderGridHeader?.(searchValue.trim(), onClick)}
        {renderHeader?.(currentLayer)}
      </div>

      {currentLayer.question ? (
        <ListQuestions
          category={currentLayer}
          value={value}
          onClick={onClick}
          tooltips={tooltips}
          helpers={helpers}
          disabledCategories={disabledCategories}
        />
      ) : (
        <ListCards
          category={currentLayer}
          categoriesDict={categoriesDict}
          value={value}
          onClick={onClick}
          tooltips={tooltips}
          helpers={helpers}
          disabledCategories={disabledCategories}
        />
      )}
    </>
  );
};

export default List;
